/**
 * The session portal (issue #321 Milestone 1, ADR 0016).
 *
 * ONE occurrence, ONE page, THREE route shells. `/instructor/sessions/:id`,
 * `/admin/teaching/sessions/:id`, and `/student/sessions/:id` all render this
 * component. The route chooses the surrounding workspace and the links back out
 * of it; it grants NO authority. Every capability below comes from
 * `viewer.allowed_actions`, which the server derived from the actual course
 * relationship — so an admin who does not teach this section reaches
 * `/admin/teaching/sessions/:id` and receives the same nondisclosing
 * "Session unavailable" as a stranger.
 *
 * THE PAGE DECIDES NOTHING ABOUT READINESS. There is no URL inspection, no
 * clock comparison, and no role branch here. `destination_state`,
 * `clock_phase`, `allowed_actions`, and `primary_action` are read. That is the
 * milestone: five surfaces each answered these questions for themselves and
 * disagreed, and the fix was one server derivation rather than one shared
 * component that each caller could still bypass.
 *
 * THE CLASS PORTAL (ADR 0035 fit row, sprint decision Q2). One portal at two
 * addresses (`/instructor/sessions/:id` and `/instructor/courses/:course?
 * session=:id`) renders one body: the edition band naming the course once and
 * the session as the `h1`; the time tile with the server-ranked act; the roll as
 * a register on the session for a teaching viewer; the session plan editor; the
 * materials and work tiles; the recording tiles. The phase changes what the
 * time tile says and which act it carries; it never moves, folds, or reorders
 * a tile. At narrow widths the same tiles stack in the same DOM order.
 *
 * EVERY ALLOWED ACT HAS EXACTLY ONE HOME. The server's ranked act is the time
 * tile's one filled control: Start session in the window, Take the roll or
 * Submit roll after it, each reaching the roll register on this page. Every
 * other act sits, quiet, in the tile whose fact it changes: plan edits on the
 * plan, Mark all present on the roll, the course in the continuation footer,
 * where the session meets edited in place below the time tile's destination
 * line. Nothing renders twice, and nothing the server withheld renders at all.
 *
 * OPENING A JOIN LINK WRITES NOTHING. There is no mutation on this page except
 * the destination editor an assigned instructor explicitly opens and the
 * coursework publish a teaching viewer explicitly chooses. The join control is
 * a plain anchor to a new tab, so the original tab keeps attendance,
 * preparation, and course context — and no attendance record, session status,
 * or "last opened" timestamp moves because somebody clicked it.
 */
import { useCourseSectionTag } from "@/hooks/use-course-section-tag";
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from "react";
import {
  ExternalLink,
  Loader2,
} from "lucide-react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router";

import {
  WithdrawnNotice,
} from "@/components/courses/workspace/WorkspacePrimitives";
import { type RollRegisterControls, type RollSubmitState } from "@/components/attendance/RollRegister";
import { ClassPlanCard, type ClassPlanControls } from "@/components/session/ClassPlanCard";
import { LearningSessionBento, TeachingSessionBento } from "@/components/session/boxes/SessionBento";
import type { Read } from "@/components/ui/box.schema";
import { CLOSE_ACTIONS, reviewWork } from "@/components/session/review/review-plan";
import { HandInBox } from "@/components/session/handin/HandInBox";
import { SessionContinuation } from "@/components/session/SessionContinuation";
import { HelpEntryLink } from "@/components/help/CourseHelp";
import { sessionHelpLabel } from "@/components/help/help-address";
import type { Session } from "@/lib/api/attendance";
import { DestinationPanel } from "@/components/session/DestinationPanel";
import { SessionChangeLinks } from "@/components/session/SessionChangeLinks";
import { followUpHasBox, followUpHoldsAct } from "@/lib/api/sessionPortal";
import { sessionChangeWords } from "@/lib/session-portal";
import { SessionDestinationEditor, type DestinationEditorControls } from "@/components/session/SessionDestinationEditor";
import { GoogleMeetSpaceControl } from "@/components/session/GoogleMeetSpaceControl";
import { ZoomMeetingControl } from "@/components/session/ZoomMeetingControl";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EditionBand } from "@/components/workspace/EditionBand";
import { useSessionWorkspace } from "@/hooks/use-session-workspace";
import { ApiError } from "@/lib/api/client";
import { MEETING_PROVIDER_LABEL, MODALITY_LABEL } from "@/lib/api/courses";
import { classTitle } from "@/lib/class-name";
import { staffDestinationDetails } from "@/lib/session-destination-copy";
import { hasAuthorizedDestinationEditorChoice } from "@/lib/session-destination-editor";
import type {
  SessionAction,
  SessionWorkspace,
  } from "@/lib/api/sessionPortal";
import {
  LEARNER_DESTINATION_MESSAGE,
  SESSION_STATUS_LABEL,
  STAFF_DESTINATION_ISSUE,
  actionLabel,
  formatInCourseZone,
} from "@/lib/session-portal";
import { courseCalendarHref, courseResourcesHref, courseWorkspaceSectionHref } from "@/navigation/course-workspace";
import { courseWorkspacePath } from "@/navigation/routes";

/**
 * The shape this file needs from `useSessionWorkspace`'s `refetch` — a
 * structural subset of react-query's real `QueryObserverResult`, so the
 * coursework flow below can read the freshly re-fetched workspace without this
 * file importing react-query directly.
 */
type WorkspaceRefetch = () => Promise<{ data?: SessionWorkspace }>;

/** Which workspace this render sits inside. Presentation and links only. */
export type PortalShell = "instructor" | "adminTeaching" | "student";

const SHELL_BASE: Record<PortalShell, string> = {
  instructor: "/instructor",
  adminTeaching: "/admin/teaching",
  student: "/student",
};

const SHELL_LABEL: Record<PortalShell, string> = {
  instructor: "My Teaching",
  adminTeaching: "My Teaching",
  student: "My Learning",
};

/**
 * The 44 px pointer floor, for a link whose TYPE must not grow (issue #465).
 *
 * The floor is the BOX, never the type: `min-h-11` sets the height and
 * `inline-flex items-center` is what makes an inline anchor honour it while
 * leaving the text at the size the design set. The same idiom
 * `SeatAllClassesLink` uses, so the portal and the seat pages reach the floor
 * the same way.
 */
const HIT_AREA = "inline-flex min-h-11 items-center";

/**
 * The same 44 px floor for a quiet act that must keep the console's 30 px line:
 * the box reaches the floor and the negative margin gives the extra back, so a
 * tile header with an act aligns with one without (AC50a measures the box).
 */
const QUIET_HIT_AREA = `${HIT_AREA} -my-[0.4375rem]`;

/** The legacy (flag-off) destination acts the time tile carries as quiet controls. */
const LEGACY_DESTINATION_REPAIRS: readonly SessionAction[] = [
  "set_meeting_link",
  "fix_meeting_link",
  "clear_session_override",
  "create_google_meet",
  "replace_google_meet",
  "create_zoom_meeting",
  "replace_zoom_meeting",
];

export function SessionPortalPage({ shell }: { shell: PortalShell }) {
  const { sessionId } = useParams();
  return <SessionWorkspaceContent sessionId={sessionId} shell={shell} />;
}

export interface CourseSessionWorkspaceProps {
  sessionId: string;
  courseId: string;
  /** An embedded course visit must hold the capacity of its owning workspace. */
  perspective?: "teaching" | "learning";
  shell: PortalShell;
  sessions: Session[];
  onSelectSession: (id: string) => void;
  onShowSessions: () => void;
  /** The owning course supplies its shared view row below the session identity. */
  courseViews?: ReactNode;
  /**
   * Inside a course page, a session that can't be read keeps the course's frame:
   * the course draws its own session box with the failure, and Try again for a
   * transient one (R1, R3). A direct session address keeps its own refusal page.
   */
  unreadSession?: (failure: { kind: "failed"; retry: () => void } | { kind: "refused" }) => ReactNode;
}

type CourseSessionNavigation = Pick<CourseSessionWorkspaceProps, "sessions" | "onSelectSession" | "onShowSessions" | "courseViews" | "unreadSession">;

/** The selected session uses the same server projection and action owners as a direct session URL. */
export function CourseSessionWorkspace({ courseId, sessionId, shell, perspective, ...navigation }: CourseSessionWorkspaceProps) {
  return <SessionWorkspaceContent sessionId={sessionId} courseId={courseId} shell={shell} perspective={perspective} navigation={navigation} />;
}

function SessionWorkspaceContent({ sessionId, courseId, shell, perspective, navigation }: {
  sessionId?: string;
  courseId?: string;
  perspective?: "teaching" | "learning";
  shell: PortalShell;
  navigation?: CourseSessionNavigation;
}) {
  const { data, isPending, isError, error, isFetching, dataUpdatedAt, refetch } =
    useSessionWorkspace(sessionId);
  const base = SHELL_BASE[shell];
  const location = useLocation();

  if (isPending) return <PortalSkeleton />;

  const authorizationFailure =
    isError && error instanceof ApiError && error.status >= 400 && error.status < 500;

  const identityMismatch = data && (data.session.id !== sessionId
    || data.session.course_id !== data.course.id
    || (courseId !== undefined && data.course.id !== courseId)
    || (perspective !== undefined && !data.viewer.capacities.includes(perspective)));

  if (!data || authorizationFailure || identityMismatch) {
    if (navigation?.unreadSession)
      return navigation.unreadSession(
        !data && !authorizationFailure ? { kind: "failed", retry: () => void refetch() } : { kind: "refused" },
      );
    // ONE refusal for a missing session, a wrong tenant, a missing course
    // relationship, and a disabled feature. Distinguishing them here would
    // undo the server's non-disclosure by rendering the difference.
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-[var(--layout-bento-gap)] px-4 pt-4 pb-16 sm:px-7 sm:pt-6">
        <header data-slot="page-frame" className="grid min-w-0 items-end gap-5 lg:grid-cols-[minmax(0,1fr)_auto]">
          <div className="grid min-w-0 gap-2">
            <h1 className="text-title font-semibold text-foreground">Session unavailable</h1>
            <WithdrawnNotice>
              {error instanceof ApiError && error.status >= 500
                ? "This session could not be loaded. Try again in a moment."
                : "This session is not available to you."}
            </WithdrawnNotice>
          </div>
          <Button asChild variant="outline">
            <Link to={base}>Back to {SHELL_LABEL[shell]}</Link>
          </Button>
        </header>
      </div>
    );
  }

  // A retired standalone address resolves only after this identity-checked read.
  if (courseId === undefined && !isError) {
    const params = new URLSearchParams(location.search);
    params.delete("view");
    params.delete("class");
    params.set("session", data.session.id);
    return <Navigate replace to={{ pathname: courseWorkspacePath(shell, data.course.id), search: params.toString(), hash: location.hash }} state={location.state} />;
  }

  return (
    <PortalBody
      key={`${data.course.id}:${data.session.id}`}
      workspace={data}
      navigation={navigation}
      shell={shell}
      staleRefresh={Boolean(!isFetching && dataUpdatedAt && isError)}
      lastUpdated={dataUpdatedAt}
      onGoogleMeetCompleted={() => void refetch()}
      onZoomMeetingCompleted={() => void refetch()}
      refetchWorkspace={refetch}
    />
  );
}

/** The links every tile's acts lead to, derived once from the shell. */
interface PortalPaths {
  base: string;
  coursePath: string;
  /** Where the session plan is edited: the session inside its course workspace. */
  classPlanPath: string;
  attendancePath: string;
  assignmentsPath: string;
  materialsPath: string;
}

/** What `ActionControl` needs beyond the act itself. */
interface ActionContext {
  workspace: SessionWorkspace;
  teaching: boolean;
  coursePath: string;
  classPlanPath: string;
  attendancePath: string;
  onEditDestination: () => void;
  onGoogleMeetCompleted: () => void;
  onZoomMeetingCompleted: () => void;
  onEditPlan?: () => void;
  /** The roll register on this page, when it is mounted: the roll acts reach it here. */
  rollControls: RefObject<RollRegisterControls | null>;
  rollMounted: boolean;
  rollSubmit: RollSubmitState;
}

function PortalBody({
  workspace,
  shell,
  staleRefresh,
  lastUpdated,
  onGoogleMeetCompleted,
  onZoomMeetingCompleted,
  refetchWorkspace,
  navigation,
}: {
  workspace: SessionWorkspace;
  shell: PortalShell;
  staleRefresh: boolean;
  lastUpdated: number;
  onGoogleMeetCompleted: () => void;
  onZoomMeetingCompleted: () => void;
  refetchWorkspace: WorkspaceRefetch;
  navigation?: CourseSessionNavigation;
}) {
  const { session, course, destination, viewer, plan, attendance } = workspace;
  const classPlanRef = useRef<ClassPlanControls>(null);
  const rollControls = useRef<RollRegisterControls | null>(null);
  const [rollSubmit] = useState<RollSubmitState>({ canSubmit: false, submitting: false });
  const [editingDestination, setEditingDestination] = useState(false);
  const [reviewOwnsAct, setReviewOwnsAct] = useState(false);
  const destinationEditorId = useId();
  const destinationEditor = useRef<DestinationEditorControls | null>(null);
  const destinationOpenerRef = useRef<HTMLElement | null>(null);
  const destinationLineRef = useRef<HTMLButtonElement | null>(null);
  const returnDestinationFocus = useRef(false);
  const base = SHELL_BASE[shell];
  const teaching = viewer.capacities.includes("teaching");
  const destinationPanelEnabled = destination.resolution !== undefined;
  const destinationEditorAvailable = teaching
    && hasAuthorizedDestinationEditorChoice(workspace);
  // The roll is a register on the session for the teaching team (decision Q2).
  // A cancelled session has no roll to take, so it carries none.
  const rollOnClass = teaching && attendance.kind === "summary" && session.status !== "cancelled";

  function openDestinationEditor() {
    destinationOpenerRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    setEditingDestination(true);
  }

  function closeDestinationEditor() {
    returnDestinationFocus.current = true;
    setEditingDestination(false);
  }

  // Closing returns focus to the line the editor opened below. The flag-off
  // portal has no such control, so focus returns to the repair that opened it.
  useEffect(() => {
    if (editingDestination || !returnDestinationFocus.current) return;
    returnDestinationFocus.current = false;
    const opener = destinationOpenerRef.current;
    (destinationPanelEnabled ? destinationLineRef.current : opener?.isConnected ? opener : null)?.focus();
  }, [editingDestination, destinationPanelEnabled]);

  const workspaceRead: Read<SessionWorkspace> = staleRefresh
    ? { status: "stale", last: workspace, readAt: new Date(lastUpdated).toISOString(), sentence: "Couldn't refresh. A later change isn't shown.", refresh: () => void refetchWorkspace() }
    : { status: "ready", value: workspace, readAt: new Date(lastUpdated || Date.now()).toISOString() };
  const coursePath = courseWorkspacePath(shell, course.id);
  // Every course view this session's acts open keeps the session selected.
  // Resources is Desk's part and ignores a session, so it carries none (#1393 R18).
  const sessionView = (view: "coursework" | "people") => courseWorkspaceSectionHref(coursePath, view, session.id);
  const materialsPath = courseResourcesHref(coursePath);
  const paths: PortalPaths = {
    base,
    coursePath,
    classPlanPath: teaching && shell !== "student" ? `${coursePath}?session=${encodeURIComponent(session.id)}` : coursePath,
    // A learner's own attendance is a part of Grades; a teacher's roll is People's attendance part (#1393 R1).
    attendancePath: shell === "student" ? `${courseWorkspacePath(shell, course.id, "grades")}&part=attendance` : `${sessionView("people")}&part=attendance`,
    assignmentsPath: sessionView("coursework"),
    materialsPath,
  };
  const actionContext: ActionContext = {
    workspace,
    teaching,
    coursePath,
    classPlanPath: paths.classPlanPath,
    attendancePath: paths.attendancePath,
    onEditDestination: openDestinationEditor,
    onGoogleMeetCompleted,
    onZoomMeetingCompleted,
    onEditPlan: teaching ? () => classPlanRef.current?.focus() : undefined,
    rollControls,
    rollMounted: rollOnClass,
    rollSubmit,
  };

  // Follow up holds the page's one filled act when the server ranks one of its acts first.
  const followUp = workspace.continuation?.follow_up;
  const followBox = followUpHasBox(followUp);
  const followHolds = followUpHoldsAct(followUp);
  // All sessions is Desk's Calendar order with this session as its context row (R10).
  const allSessionsPath = courseCalendarHref(coursePath, { session: session.id });
  /** An allowed act that is not the ranked one: it renders in its own tile. */

  // Close's act ranks before Review's; an attempt opened by hand in Review takes it (board 3).
  const closeActRanked = viewer.primary_action !== null && CLOSE_ACTIONS.includes(viewer.primary_action)
    || viewer.primary_action === "publish_session_plan" && plan.publication_task === "close";
  const reviewShown = teaching && session.status !== "cancelled" && reviewWork(workspace).length > 0;

  // Where it meets, edited in place by the teaching team: Teach's body.
  const d4Teaching = destination.resolution !== undefined;
  const staffDetails = staffDestinationDetails(workspace);
  const legacyRepairs = !d4Teaching ? LEGACY_DESTINATION_REPAIRS.filter(action => viewer.allowed_actions.includes(action)) : [];
  const teachBody = destinationEditorAvailable || staffDetails.length > 0 || legacyRepairs.length > 0 ? (
    <div className="grid gap-2" data-session-destination-body>
      {d4Teaching
        ? <DestinationPanel workspace={workspace} teaching edit={destinationEditorAvailable ? { expanded: editingDestination, controls: destinationEditorId, onToggle: () => editingDestination ? destinationEditor.current?.close() : openDestinationEditor(), toggleRef: destinationLineRef } : undefined} />
        : <><LegacyWhereLine destination={destination} /><DestinationNotices workspace={workspace} teaching /></>}
      {destinationEditorAvailable && <SessionDestinationEditor id={destinationEditorId} workspace={workspace} open={editingDestination} onOpenChange={next => next ? setEditingDestination(true) : closeDestinationEditor()}
        onGoogleMeetCompleted={onGoogleMeetCompleted} onZoomMeetingCompleted={onZoomMeetingCompleted} controlsRef={destinationEditor} />}
      {staffDetails.length > 0 && <ul className="grid gap-1 text-meta text-muted-foreground">{staffDetails.map(detail => <li key={detail}>{detail}</li>)}</ul>}
      {legacyRepairs.length > 0 && <div className="flex flex-wrap gap-2" data-session-destination-repairs>{legacyRepairs.map(action => <ActionControl key={action} action={action} variant="outline" {...actionContext} />)}</div>}
    </div>
  ) : undefined;

  if (teaching) return (
    <div data-session-portal={session.id} className="mx-auto flex w-full max-w-[90rem] flex-col gap-[var(--layout-bento-gap)] px-4 pt-4 pb-16 sm:px-7 sm:pt-6">
      <ClassBand workspace={workspace} base={base} navigation={navigation} />
      {navigation?.courseViews}
      <ClassPlanCard workspace={workspace} canEdit={course.term_open && (viewer.allowed_actions.includes("create_session_plan") || viewer.allowed_actions.includes("edit_session_plan"))}
        controlsRef={classPlanRef} renderTasks={parts => (
          <TeachingSessionBento workspace={workspace} read={workspaceRead} parts={parts} courseworkPath={paths.assignmentsPath} allSessionsPath={allSessionsPath} followQuiet={reviewOwnsAct} teachBody={teachBody} teachChanges={destinationEditorAvailable || legacyRepairs.length > 0}
            reviewShown={reviewShown} closeRanks={followHolds || closeActRanked} onReviewOwnsAct={setReviewOwnsAct}
            after={<SessionChangeLinks workspace={workspace} allSessionsPath={allSessionsPath} />} />
        )} />
      <SessionContinuation workspace={workspace} onSelectSession={navigation?.onSelectSession} cancelledInFoot={!followBox} onRetry={() => void refetchWorkspace()} />
    </div>
  );

  // The learner's page: Get ready and After class in time order, then the
  // foot. When it meets and where is read only; Join leads only when the
  // server ranks it.
  const where = destination.resolution !== undefined
    ? <DestinationPanel workspace={workspace} teaching={false} />
    : <><LegacyWhereLine destination={destination} /><DestinationNotices workspace={workspace} teaching={false} /></>;
  return (
    <div data-session-portal={session.id} className="mx-auto flex w-full max-w-[90rem] flex-col gap-[var(--layout-bento-gap)] px-4 pt-4 pb-16 sm:px-7 sm:pt-6">
      <ClassBand workspace={workspace} base={base} navigation={navigation} />
      {navigation?.courseViews}
      <LearningSessionBento read={workspaceRead} where={where} onRetry={() => void refetchWorkspace()}
        courseworkPath={paths.assignmentsPath} allSessionsPath={allSessionsPath}
        after={<HandInBox workspace={workspace} onRetry={() => void refetchWorkspace()} quiet={followHolds} />} />
      {/* Help from where the learner is stuck (#1294): opens their course Help about this exact session. */}
      <HelpEntryLink courseHref={paths.coursePath} reference={{ kind: "session", id: session.id, label: sessionHelpLabel(session) }} />
      <SessionContinuation workspace={workspace} onSelectSession={navigation?.onSelectSession} cancelledInFoot={!followBox} onRetry={() => void refetchWorkspace()} />
    </div>
  );
}

/**
 * The edition band (ADR 0035): the course's cover carrying its code, its title
 * as the eyebrow, the session as the page `h1`, and one meta line with only what
 * the time tile does not say: the session's state and its room. When it meets is
 * the time tile's fact, said there once. Its one quiet act reaches the session
 * index: the course picker inside the course frame, All sessions outside it.
 * Inside the frame the frame's own course band is not drawn, so this is the
 * one identity zone and the only `h1`.
 */
function ClassBand({
  workspace,
  base,
  navigation,
}: {
  workspace: SessionWorkspace;
  base: string;
  navigation?: CourseSessionNavigation;
}) {
  const { session, course, destination } = workspace;
  const sectionTag = useCourseSectionTag(course.id, workspace.viewer.capacities.includes("teaching"));
  const bandRef = useRef<HTMLDivElement>(null);
  const focusedSession = useRef<string | null>(null);
  const [searchParams] = useSearchParams();
  const embedded = !!navigation;
  useEffect(() => {
    if (!embedded || focusedSession.current === session.id) return;
    focusedSession.current = session.id;
    // Exact records and guidance editors own their own focus. Closing one
    // must return to its opener rather than re-focusing the session heading.
    if (searchParams.has("assignment") || searchParams.has("unit")
      || document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
    const heading = bandRef.current?.querySelector("h1");
    if (!heading) return;
    if (!heading.hasAttribute("tabindex")) heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }, [embedded, session.id, searchParams]);

  // The place when known: the room of an in-person or hybrid session.
  const place = destination.modality !== "online" ? destination.location : null;
  const stated = session.status !== "scheduled";

  return (
    <div data-session-band ref={bandRef} className={`grid min-w-0 [&_h1]:outline-none [&_h1:focus-visible]:rounded-xs [&_h1:focus-visible]:ring-2 [&_h1:focus-visible]:ring-ring ${workspace.viewer.capacities.includes("teaching") ? "max-sm:[&_[data-slot=edition-cover]]:size-11 max-sm:[&_[data-slot=edition-cover]>span]:hidden" : ""}`}>
      <EditionBand
        code={course.code}
        eyebrow={<>{workspace.viewer.capacities.includes("teaching") && <span data-mobile-course-code className="sm:hidden">{course.code} · </span>}{course.title}{sectionTag && ` · ${sectionTag}`}</>}
        title={classTitle(session, course.timezone)}
        meta={(stated || place || session.latest_change) && (
          <>
            <StateWord session={session} includeNote={workspace.viewer.capacities.includes("teaching")} />
            {(stated || (session.latest_change && session.status !== "held")) && place ? " · " : ""}
            {place}
            {session.status === "cancelled" && workspace.viewer.capacities.includes("teaching") && <> · {formatInCourseZone(session.starts_at, course.timezone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} to {formatInCourseZone(session.ends_at, course.timezone, { hour: "numeric", minute: "2-digit" })}</>}
          </>
        )}
        action={navigation ? (
          <Button type="button" variant="subtle" size="sm" className={QUIET_HIT_AREA} onClick={navigation.onShowSessions}>
            All sessions
          </Button>
        ) : (
          <Button asChild variant="subtle" size="sm" className={QUIET_HIT_AREA}>
            <Link to={`${base}/sessions`}>My sessions</Link>
          </Button>
        )}
      />
    </div>
  );
}

/**
 * The session's institutional state as one word leading the band's meta line
 * (ADR 0035, decision 6: a state is plain meta text, never a chip), said
 * nowhere else on the page. A scheduled session needs none: the time tile's day
 * and figure already say it. Cancelled is the page's one interruption, in
 * warning ink.
 */
function StateWord({ session, includeNote }: { session: SessionWorkspace["session"]; includeNote: boolean }) {
  if (session.latest_change && session.status !== "held") return <span className={session.status === "cancelled" ? "text-status-warning" : undefined}>{sessionChangeWords(session.latest_change, includeNote)}</span>;
  if (session.status === "scheduled") return null;
  return (
    <span className={session.status === "cancelled" ? "text-status-warning" : undefined}>
      {SESSION_STATUS_LABEL[session.status]}
    </span>
  );
}

/**
 * The flag-off destination, in one line: modality, then the provider where it
 * applies. The band names the room; a session with none says so here, once. An
 * in-person session says plainly that it has no link (design 4f). It reads no URL.
 */
function LegacyWhereLine({ destination }: { destination: SessionWorkspace["destination"] }) {
  const provider = destination.meeting_provider && destination.modality !== "in_person"
    ? MEETING_PROVIDER_LABEL[destination.meeting_provider]
    : null;
  if (destination.modality === "online") {
    return (
      <p className="text-ui font-medium text-foreground-soft">
        <span>{MODALITY_LABEL.online}</span>
        {provider && <> · <span>{provider}</span></>}
      </p>
    );
  }
  return (
    <section aria-label="Where this session meets" className="grid gap-0.5">
      {destination.modality === "in_person"
        ? <p className="text-ui font-medium text-foreground-soft">This session meets in person. There is no meeting link.</p>
        : (
            <>
              <p className="text-ui text-foreground-soft">
                <span className="text-muted-foreground">Remote</span>{" "}
                <span className="font-medium">{provider ?? "the meeting provider"}</span>
              </p>
              <p className="text-meta text-muted-foreground">
                {destination.destination_state === "ready"
                  ? "Learners joining remotely have a meeting link."
                  : "There is no meeting link for remote learners yet."}
              </p>
            </>
          )}
      {!destination.location && (
        <p className="text-meta text-muted-foreground">
          {destination.modality === "in_person" ? "No room is recorded." : "No room is recorded for the in-person half."}
        </p>
      )}
    </section>
  );
}

/**
 * The flag-off destination notices: readiness is a server-derived fact, so
 * the teaching summary is stated in the moment and exact provider diagnostics
 * stay one disclosure away. Empty, it takes no space.
 */
function DestinationNotices({
  workspace,
  teaching,
}: {
  workspace: SessionWorkspace;
  teaching: boolean;
}) {
  const { destination } = workspace;
  const learnerMessage = LEARNER_DESTINATION_MESSAGE[destination.destination_state];
  const managedMeet = destination.managed_google_meet;
  const managedZoom = destination.managed_zoom;
  // `operation` comes from the server. The fallback keeps pre-UI-B fixtures
  // readable without ever changing what this viewer is allowed to do.
  const managedZoomOperation = managedZoom?.operation ?? "create";
  const hasManagedMeetRecovery = managedMeet?.state === "failed" || managedMeet?.state === "unknown";
  const hasManagedMeetZoomBlocker = managedMeet?.state === "pending"
    || managedMeet?.state === "ready"
    || managedMeet?.state === "unknown";
  const hasManagedZoomRecovery = managedZoom?.state === "failed" || managedZoom?.state === "unknown";
  const hasManagedZoomScheduleDrift = managedZoom?.state === "ready" && managedZoom.schedule_state === "drifted";
  const hasManagedZoomCurrentSchedule = managedZoom?.state === "ready" && managedZoom.schedule_state === "current";
  const hasManualZoomDestination = managedZoom?.manual_destination === true;
  const destinationRecoverySummary = destination.destination_state === "not_applicable"
    && destination.destination_issue === "remote_provider_for_in_person"
    ? "This session meets in person. A stale remote link can be cleared."
    : destination.destination_state === "missing_url"
      ? "This session needs a meeting link."
      : destination.destination_issue
        ? "Meeting link needs correction."
        : null;
  const managedMeetRecoverySummary = hasManagedMeetRecovery
    ? "A Google Meet creation attempt needs recovery."
    : null;
  const managedZoomRecoverySummary = hasManagedZoomRecovery
    ? "A Zoom meeting creation attempt needs recovery."
    : null;
  const managedZoomScheduleSummary = hasManagedZoomScheduleDrift
    ? "This managed Zoom meeting no longer matches the current session schedule."
    : null;

  return (
    <div className="grid gap-3 empty:hidden">
      {/* Readiness is a server-derived fact. The teaching summary is useful in
          the moment; exact provider diagnostics remain available on demand.
          Each notice keeps its `role="status"`; the Alert supplies the surface. */}
      {(teaching
        ? (
          <>
            {destinationRecoverySummary && (
              <Alert variant="warning" role="status">
                <AlertDescription>
                  <p>{destinationRecoverySummary}</p>
                  {destination.destination_issue && (
                    <details>
                      <summary className="cursor-pointer font-medium">View meeting-link details</summary>
                      <div className="grid gap-2 pt-2">
                        <p>{STAFF_DESTINATION_ISSUE[destination.destination_issue]}</p>
                      </div>
                    </details>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {managedMeetRecoverySummary && (
              <Alert variant="warning" role="status">
                <AlertDescription>
                  <p>{managedMeetRecoverySummary}</p>
                  <details>
                    <summary className="cursor-pointer font-medium">View Google Meet recovery details</summary>
                    <div className="grid gap-2 pt-2">
                      {managedMeet?.state === "failed" && (
                        <p>Google Meet creation failed. You may retry or use a manual meeting link.</p>
                      )}
                      {managedMeet?.state === "unknown" && (
                        <p>Google Meet creation has an unknown outcome. Use a manual meeting link and ask an operator to reconcile the provider record before retrying.</p>
                      )}
                    </div>
                  </details>
                </AlertDescription>
              </Alert>
            )}
            {hasManagedMeetZoomBlocker && (
              <Alert variant="warning" role="status">
                <AlertDescription>
                  Managed Google Meet already owns this session. Zoom creation and replacement are unavailable.
                </AlertDescription>
              </Alert>
            )}
            {managedZoomRecoverySummary && (
              <Alert variant="warning" role="status">
                <AlertDescription>
                  <p>{managedZoomRecoverySummary}</p>
                  <details>
                    <summary className="cursor-pointer font-medium">View Zoom recovery details</summary>
                    <div className="grid gap-2 pt-2">
                      {managedZoom?.state === "failed" && (
                        <p>
                          {managedZoomOperation === "replace"
                            ? "Zoom meeting replacement failed. You may retry or use a manual meeting link."
                            : "Zoom meeting creation failed. You may retry or use a manual meeting link."}
                        </p>
                      )}
                      {managedZoom?.state === "unknown" && (
                        <p>
                          {managedZoomOperation === "replace"
                            ? "Zoom meeting replacement has an unknown outcome. Do not retry. Use a manual meeting link and ask an operator to reconcile the provider record."
                            : "Zoom meeting creation has an unknown outcome. Do not retry. Use a manual meeting link and ask an operator to reconcile the provider record."}
                        </p>
                      )}
                    </div>
                  </details>
                </AlertDescription>
              </Alert>
            )}
            {managedZoomScheduleSummary && (
              <Alert variant="warning" role="status">
                <AlertDescription>
                  <p>{managedZoomScheduleSummary}</p>
                  <details>
                    <summary className="cursor-pointer font-medium">View Zoom schedule repair details</summary>
                    <div className="grid gap-2 pt-2">
                      <p>Review the session time and explicitly replace the meeting when local Zoom provisioning is current. No automatic change was sent to Zoom.</p>
                    </div>
                  </details>
                </AlertDescription>
              </Alert>
            )}
            {hasManagedZoomCurrentSchedule && (
              <Alert variant="success" role="status">
                <AlertDescription>
                  This managed Zoom meeting is ready and matches the current session schedule.
                </AlertDescription>
              </Alert>
            )}
            {hasManualZoomDestination && (
              <Alert variant="info" role="status">
                <AlertDescription>
                  A manual Zoom meeting link is active.
                </AlertDescription>
              </Alert>
            )}
            {managedZoom?.shared_host_conflict && (
              <Alert variant="warning" role="status">
                <AlertDescription>
                  Another IMAM-managed session overlaps on the shared Zoom host.
                </AlertDescription>
              </Alert>
            )}
          </>
        )
        : learnerMessage && (
            <Alert variant="info" role="status">
              <AlertDescription>{learnerMessage}</AlertDescription>
            </Alert>
          ))}

      {teaching && managedMeet?.state === "pending" && (
        <Alert variant="info" role="status">
          <AlertDescription>
            Google Meet creation is in progress. Do not start another creation attempt.
          </AlertDescription>
        </Alert>
      )}
      {teaching && managedZoom?.state === "pending" && (
        <Alert variant="info" role="status">
          <AlertDescription>
            {managedZoomOperation === "replace"
              ? "Zoom meeting replacement is in progress. Do not start another replacement attempt."
              : "Zoom meeting creation is in progress. Do not start another creation attempt."}
          </AlertDescription>
        </Alert>
      )}
      {teaching && destination.effective_source === "session_override" && (
        <p className="text-meta text-muted-foreground">
          This session overrides the course default.
        </p>
      )}
    </div>
  );
}

function ActionControl({
  action,
  workspace,
  teaching,
  coursePath,
  classPlanPath,
  attendancePath,
  onEditDestination,
  onGoogleMeetCompleted,
  onZoomMeetingCompleted,
  onEditPlan,
  rollControls,
  rollMounted,
  rollSubmit,
  variant,
}: ActionContext & {
  action: SessionAction;
  variant: "default" | "outline";
}) {
  const { destination } = workspace;
  const label = actionLabel(action, teaching);
  // The ranked act is ONE filled control and it spans the band that holds it;
  // a supporting act stays a quiet, left-aligned row in the list below. The
  // WHICH is still entirely the server's — only the weight is decided here.
  const size = variant === "default" ? "lg" : "default";
  const className = variant === "default" ? "w-full" : "justify-start";

  switch (action) {
    case "join_meeting":
      // A plain anchor to a new tab. No handler, no beacon, no state change —
      // which is how "opening a join link writes no attendance record" is true
      // structurally rather than by everyone remembering.
      return (
        <div className="grid gap-2">
          <Button asChild size="lg" variant={variant} className={className}>
            <a href={destination.join_url ?? undefined} target="_blank" rel="noopener noreferrer">
              {destination.join_label ?? label}
              <ExternalLink aria-hidden className="size-4" strokeWidth={1.75} />
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </Button>
          {/* Said in the open, beneath the control it describes, because the
              shipped behaviour is already exactly this: a plain new-tab anchor,
              and this tab keeps preparation, attendance, and course context. */}
          <p className="text-ui text-muted-foreground">
            Opens in a new tab - this page stays with you.
          </p>
        </div>
      );
    case "set_meeting_link":
    case "fix_meeting_link":
    case "clear_session_override":
      return (
        <Button size={size} variant={variant} className={className} onClick={onEditDestination}>
          {label}
        </Button>
      );
    case "create_google_meet":
    case "replace_google_meet":
      return (
        <GoogleMeetSpaceControl
          sessionId={workspace.session.id}
          replace={action === "replace_google_meet"}
          variant={variant}
          size={size}
          className={className}
          onCompleted={onGoogleMeetCompleted}
        />
      );
    case "create_zoom_meeting":
    case "replace_zoom_meeting":
      return (
        <ZoomMeetingControl
          key={[
            destination.managed_zoom?.state ?? "absent",
            destination.managed_zoom?.failure_classification ?? "",
            destination.managed_zoom?.created_at ?? "",
            destination.managed_zoom?.completed_at ?? "",
          ].join(":")}
          sessionId={workspace.session.id}
          replace={action === "replace_zoom_meeting"}
          variant={variant}
          size={size}
          className={className}
          onCompleted={onZoomMeetingCompleted}
          onRecoveryRequired={onZoomMeetingCompleted}
        />
      );
    case "take_attendance":
      // The roll is on this page: the act moves to it, no navigation. Without
      // a roll here (no summary for this viewer) it reaches the People view.
      if (!rollMounted) {
        return (
          <Button asChild size={size} variant={variant} className={className}>
            <Link to={attendancePath}>{label}</Link>
          </Button>
        );
      }
      return (
        <Button type="button" size={size} variant={variant} className={className} onClick={() => rollControls.current?.focus()}>
          {label}
        </Button>
      );
    case "complete_attendance":
      // Submit roll is the same act the register's own foot makes elsewhere:
      // the draft on this page, on the version it was made against.
      if (!rollMounted) {
        return (
          <Button asChild size={size} variant={variant} className={className}>
            <Link to={attendancePath}>{label}</Link>
          </Button>
        );
      }
      return (
        <Button
          type="button"
          size={size}
          variant={variant}
          className={className}
          disabled={!rollSubmit.canSubmit}
          onClick={() => rollControls.current?.submit()}
        >
          {rollSubmit.submitting ? "Submitting…" : label}
        </Button>
      );
    case "create_session_plan":
    case "edit_session_plan":
      if (onEditPlan) return <Button size={size} variant={variant} className={className} onClick={onEditPlan}>{label}</Button>;
      return (
        <Button asChild size={size} variant={variant} className={className}>
          <Link to={classPlanPath}>{label}</Link>
        </Button>
      );
    case "view_course":
      return (
        <Button asChild size={size} variant={variant} className={className}>
          <Link to={coursePath}>{label}</Link>
        </Button>
      );
    case "review_preparation":
      return (
        <Button asChild size={size} variant={variant} className={className}>
          <a href="#session-plan">{label}</a>
        </Button>
      );
    case "view_location":
      // The band names the room; a control that only repeated it is no act.
      return null;
  }
}

/** No guessed time, status, or destination while loading: the bento's shape only. */
function PortalSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-[90rem] flex-col gap-[var(--layout-bento-gap)] px-4 pt-4 pb-16 sm:px-7 sm:pt-6" aria-busy="true">
      <span className="sr-only" role="status">
        <Loader2 aria-hidden className="size-4 animate-spin" /> Loading this session
      </span>
      <div className="flex items-center gap-5">
        <Skeleton className="h-[5.5rem] w-[14.5rem] shrink-0 rounded-xl" />
        <div className="grid flex-1 gap-2">
          <Skeleton className="h-[1.125rem] w-40" />
          <Skeleton className="h-9 w-2/3" />
          <Skeleton className="h-5 w-1/2" />
        </div>
      </div>
      <div className="grid gap-[var(--layout-bento-gap)] xl:grid-cols-12">
        <Skeleton className="h-[11.75rem] rounded-xl xl:col-span-4" />
        <Skeleton className="h-[11.75rem] rounded-xl xl:col-span-8" />
      </div>
      <div className="grid gap-[var(--layout-bento-gap)] xl:grid-cols-12">
        <Skeleton className="h-72 rounded-xl xl:col-span-8" />
        <Skeleton className="h-72 rounded-xl xl:col-span-4" />
      </div>
    </div>
  );
}
